'use client';

import {
  Button,
  ButtonGroup,
  CardTable,
  Flex,
  Text,
  Dialog,
  Heading,
  Checkbox,
  CellCheckbox,
  CellContainer,
} from '@/_components';
import {
  ReactNode,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  type FC,
} from 'react';
import { deleteDocument } from '@/_api/client-documents';
import styles from './DocumentsTable.module.css';
import { DOCUMENTS_TABLE_HEADER_MAP } from '@/_constants/documents';
import {
  ColumnDef,
  getSortedRowModel,
  getCoreRowModel,
  useTable,
  SortingState,
} from '@tanstack/react-table';
import type { DocumentsTableProps } from './DocumentsTable.types';
import { FileOrFolderMovingOrCopyingDialog } from './components/FileOrFolderMovingOrCopyingDialog';
import { DocumentsCell } from './components/DocumentsCell';
import { DocumentItem } from '@/_types/models/documents';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import type { DocumentsColumnType } from '@/_types/documents';
import { defaultSortingFn } from '@/_utils/sorting';
import { RenameDocumentDialog } from './components/RenameDocumentDialog';
import { EditDescriptionDialog } from './components/EditDescriptionDialog';
import { DocumentBulkActionBar } from './components/DocumentBulkActionBar';
import { EditTagsDialog } from './components/EditTagsDialog';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getIsClientActionsFolderDisabled } from '@/_utils/documents';
import { AttachToQuestionnaireDialog } from './components/AttachToQuestionnaireDialog';
import { getClasses } from '@/_utils/classnames';

const COLUMNS: Array<{
  key: DocumentsColumnType;
  sortingFn?: any;
}> = [
  { key: 'select' },
  { key: 'name', sortingFn: defaultSortingFn },
  { key: 'description' },
  { key: 'tags' },
  { key: 'updated_at', sortingFn: defaultSortingFn },
  { key: 'created_at', sortingFn: defaultSortingFn },
  { key: 'actions' },
];

const COLUMN_STYLES: Record<DocumentsColumnType, string> = {
  select: styles.documentsTable__columnSelect,
  name: styles.documentsTable__columnName,
  description: styles.documentsTable__columnDescription,
  tags: styles.documentsTable__columnTags,
  updated_at: styles.documentsTable__columnUpdatedAt,
  size: styles.documentsTable__columnSize,
  created_at: styles.documentsTable__columnCreatedAt,
  actions: styles.documentsTable__columnActions,
};

export const DocumentsTable: FC<DocumentsTableProps> = ({
  companyId,
  documents = [],
  isPreview,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token, isAdvisor } = useContext(UserContext);
  const { isLoading, loadRootDirectories, openDirectory, reloadFiles } =
    useContext(DocumentsContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [editingDescription, setEditingDescription] = useState<
    DocumentItem | undefined
  >(undefined);
  const [deleting, setDeleting] = useState<DocumentItem | undefined>(undefined);
  const [renaming, setRenaming] = useState<DocumentItem | undefined>(undefined);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isMovingOrCopying, setIsMovingOrCopying] = useState<any>(undefined);
  const [isSelectHeadCellChecked, setIsSelectHeadCellChecked] = useState(false);
  const [editTags, setEditTags] = useState<DocumentItem | undefined>(undefined);
  const [attachItem, setAttachItem] = useState<DocumentItem | undefined>(
    undefined,
  );

  const shouldDisableClientActions: boolean = getIsClientActionsFolderDisabled(
    isAdvisor,
    preferences,
    openDirectory,
  );

  const tableColumns = useMemo<ColumnDef<any>[]>(
    () =>
      COLUMNS.map(({ key, sortingFn }) => {
        return {
          accessorKey: key,
          cell: (info) => (
            <div className={`column-${key}`}>
              {info.getValue() as ReactNode}
            </div>
          ),
          header: () => key,
          sortingFn,
        };
      }),
    [],
  );

  const table = useTable({
    columns: tableColumns,
    data: documents,
    // debugTable: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const onFileDelete = (item: DocumentItem) => setDeleting(item);
  const onItemCopy = (item: DocumentItem) =>
    setIsMovingOrCopying({ selectedItems: [item], isMoving: false });
  const onItemMove = (item: DocumentItem) =>
    setIsMovingOrCopying({ selectedItems: [item], isMoving: true });
  const onFileRename = (item: DocumentItem) => setRenaming(item);
  const onEditDescription = (item: DocumentItem) => setEditingDescription(item);
  const onEditTags = (item: DocumentItem) => setEditTags(item);
  const onAttachToQuestionnaire = (item: DocumentItem) => setAttachItem(item);

  const onCloseDelete = () => setDeleting(undefined);
  const onCloseRename = () => setRenaming(undefined);
  const onCloseEditDescription = () => setEditingDescription(undefined);
  const onCloseEditTags = () => setEditTags(undefined);
  const onCloseAttach = () => setAttachItem(undefined);

  const onDelete = async () => {
    const isFolder: boolean = Boolean(deleting?.type === 'folder');

    try {
      await deleteDocument({
        token,
        path: openDirectory,
        documentName: deleting?.name!,
        isFolder,
        companyId,
      });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onCloseDelete();
      reloadFiles();

      // Special case where deleting from root directory needs an explicit reload
      if (openDirectory === '' || openDirectory === '/') {
        loadRootDirectories(true);
      }
    }
  };

  const [selectedDocumentNames, setSelectedDocumentNames] = useState<string[]>(
    [],
  );

  const toggleSelect = useCallback((documentName: string) => {
    setSelectedDocumentNames((prev) => {
      if (prev.includes(documentName)) {
        return prev.filter((name) => name !== documentName);
      } else {
        return [...prev, documentName];
      }
    });
  }, []);

  const selectedDocumentItems = useMemo(() => {
    return documents.filter((d) => selectedDocumentNames.includes(d.name));
  }, [selectedDocumentNames, documents]);

  const refresh = useCallback(async () => {
    await reloadFiles();
    setSelectedDocumentNames([]);
  }, [reloadFiles]);

  const onItemsCopy = useCallback((selectedItems: DocumentItem[]) => {
    setIsMovingOrCopying({ selectedItems, isMoving: false });
  }, []);

  const onItemsMove = useCallback((selectedItems: DocumentItem[]) => {
    setIsMovingOrCopying({ selectedItems, isMoving: true });
  }, []);

  useEffect(() => {
    setSelectedDocumentNames([]);

    return () => {
      setSelectedDocumentNames([]);
    };
  }, [openDirectory]);

  useEffect(() => {
    if (isSelectHeadCellChecked) {
      setSelectedDocumentNames(documents.map((d) => d.name));
    } else {
      setSelectedDocumentNames([]);
    }
  }, [isSelectHeadCellChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  const getShouldShow = (header: string): boolean => {
    if (header === 'tags') {
      return isAdvisor && preferences.document_tags_enabled!;
    }

    if (header === 'description') {
      return isAdvisor;
    }

    return true;
  };

  const allSelected: boolean = documents
    .map((d) => d.name)
    .every((name) => selectedDocumentNames.includes(name));

  return (
    <>
      {!!isMovingOrCopying && (
        <FileOrFolderMovingOrCopyingDialog
          companyId={companyId}
          isMoving={!!isMovingOrCopying?.isMoving}
          isOpen={!!isMovingOrCopying}
          selectedItems={isMovingOrCopying?.selectedItems}
          onClose={() => setIsMovingOrCopying(undefined)}
          onUpdated={() => setSelectedDocumentNames([])}
        />
      )}

      <Dialog
        isOpen={!!deleting}
        onClose={onCloseDelete}
        title="Confirm Delete"
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            <Text type="paragraph">
              Are you sure you want to delete <b>{deleting?.name}</b>?
            </Text>
            <ButtonGroup align="right">
              <Button
                value="Cancel"
                intent="secondary"
                onClick={onCloseDelete}
              />
              <Button value="Delete" intent="danger" onClick={onDelete} />
            </ButtonGroup>
          </Flex>
        </Dialog.Content>
      </Dialog>

      <EditDescriptionDialog
        item={editingDescription}
        onClose={onCloseEditDescription}
      />

      <EditTagsDialog item={editTags} onClose={onCloseEditTags} />

      <RenameDocumentDialog item={renaming} onClose={onCloseRename} />

      <AttachToQuestionnaireDialog item={attachItem} onClose={onCloseAttach} />

      {selectedDocumentItems.length > 0 && (
        <DocumentBulkActionBar
          companyId={companyId}
          currentPath={openDirectory}
          selectedDocumentItems={selectedDocumentItems}
          shouldDisableClientActions={shouldDisableClientActions}
          clearSelections={() => setSelectedDocumentNames([])}
          refresh={refresh}
          onItemsCopy={onItemsCopy}
          onItemsMove={onItemsMove}
        />
      )}

      <CardTable isLoading={isLoading}>
        <table className="sorabanTable">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isSortable: boolean = !!COLUMNS.find(
                    (c) => c.key === (header.id as DocumentsColumnType),
                  )?.sortingFn;
                  const isLastColumn: boolean =
                    index === headerGroup.headers.length - 1;
                  const thClasses: string = getClasses(
                    COLUMN_STYLES[header.id as DocumentsColumnType],
                    {
                      sorabanTable__fixedColumn: isLastColumn,
                    },
                  );

                  const shouldShow: boolean = getShouldShow(header.column.id);

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width:
                          header.id === 'select' ? '32px' : header.getSize(),
                      }}
                      className={thClasses}
                    >
                      {header.id === 'select' ? (
                        <CellCheckbox isHeader={true}>
                          <Checkbox
                            label="Select"
                            isLabelHidden={true}
                            isChecked={allSelected}
                            onChange={() => {
                              setIsSelectHeadCellChecked((prev) => !prev);
                            }}
                          />
                        </CellCheckbox>
                      ) : header.isPlaceholder ? null : (
                        <CellContainer>
                          <Flex
                            isYCentered={true}
                            gap={2}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <Heading level={7} isSameLine={true}>
                              {shouldShow &&
                                DOCUMENTS_TABLE_HEADER_MAP[
                                  header.column.id as DocumentsColumnType
                                ]}
                            </Heading>
                            {isSortable && (
                              <ButtonGroup>
                                {{
                                  asc: (
                                    <Button
                                      value="x"
                                      intent="ghost"
                                      size="tiny"
                                      isIconOnly={true}
                                      iconLeft="arrow-up"
                                    />
                                  ),
                                  desc: (
                                    <Button
                                      value="x"
                                      intent="ghost"
                                      size="tiny"
                                      isIconOnly={true}
                                      iconLeft="arrow-down"
                                    />
                                  ),
                                }[header.column.getIsSorted() as string] ?? (
                                  <Button
                                    value="minus"
                                    intent="ghost"
                                    size="tiny"
                                    isIconOnly={true}
                                    iconLeft="minus"
                                  />
                                )}
                              </ButtonGroup>
                            )}
                          </Flex>
                        </CellContainer>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id} data-testid="DocumentsTable-document-row">
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const document = cell?.row?.original;
                    const isFolder: boolean = document?.type === 'folder';

                    const isLastColumn: boolean =
                      cellIndex === row.getVisibleCells().length - 1;

                    return (
                      <DocumentsCell
                        key={cell.id}
                        className={
                          COLUMN_STYLES[cell.column.id as DocumentsColumnType]
                        }
                        isFolder={isFolder}
                        onFileDelete={onFileDelete}
                        onItemCopy={onItemCopy}
                        onItemMove={onItemMove}
                        onEditDescription={onEditDescription}
                        onFileRename={onFileRename}
                        onEditTags={onEditTags}
                        onAttachToQuestionnaire={onAttachToQuestionnaire}
                        type={cell.column.id as DocumentsColumnType}
                        document={document}
                        isPreview={isPreview}
                        isSelected={selectedDocumentNames.includes(
                          document.name,
                        )}
                        isLastColumn={isLastColumn}
                        shouldDisableClientActions={shouldDisableClientActions}
                        toggleSelect={toggleSelect}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardTable>
    </>
  );
};
